<template>
    <div class="congress-tickets-editor">
        <!-- Empty State -->
        <div v-if="!modelValue || modelValue.length === 0" class="empty-state congress-schedule-empty-state">
            <p class="mb-4">
                {{ lang('congresses::congresses.tickets.empty') }}
            </p>
            <Button type="button" size="sm" color="brand" variant="inline" @click="addTicket">
                <span class="action-icon">
                    <Icon icon="phosphor/regular/ticket"></Icon>
                </span>
                <span class="action-label-text">
                    {{ lang('congresses::congresses.tickets.add_first') }}
                </span>
            </Button>
        </div>

        <!-- Tickets List -->
        <div v-else class="tickets-list">
            <Sortable :list="modelValue" item-key="_id" tag="div" @end="onReordered">
                <template #item="{ element: ticket, index }">
                    <div :key="`${ticket._id}-${index}`" class="tree-line-item grid grid-cols-12">
                        <div class="col-span-1">
                            <TreeLine />
                        </div>
                        <div class="col-span-11">
                            <TicketCard
                                :key="ticket._id"
                                v-model="modelValue[index]"
                                :index="index"
                                @remove="removeTicket(index)"
                            />
                        </div>
                    </div>
                </template>
            </Sortable>

            <!-- Add Button -->
            <div class="congress-schedule-after-event-list-actions">
                <Button type="button" size="sm" color="brand" variant="inline" @click="addTicket">
                    <span class="action-icon">
                        <Icon icon="phosphor/regular/ticket"></Icon>
                    </span>
                    <span class="action-label-text">
                        {{ lang('congresses::congresses.tickets.add') }}
                    </span>
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid';
import { lang } from '@/lang/composable';
import Sortable from '@src/Common/Components/Sortable.vue';
import Button from '@src/Common/Components/Button.vue';
import TreeLine from '@src/Common/Components/TreeLine.vue';
import TicketCard from './TicketCard.vue';
import type { Ticket } from '../types';

const modelValue = defineModel<Ticket[]>();

// Ensure all items have _id
const ensureIds = (tickets: Ticket[]) => {
    return tickets.map((ticket) => ({
        ...ticket,
        _id: ticket._id || uuidv4(),
    }));
};

if (!modelValue.value) {
    modelValue.value = [];
} else {
    modelValue.value = ensureIds(modelValue.value);
}

const addTicket = () => {
    modelValue.value = [
        ...modelValue.value,
        {
            _id: uuidv4(),
            name: '',
            description: '',
            price: 0,
            currency: 'CZK',
            type: 'ticket',
            is_active: 'active',
            is_vip: false,
            sort_order: modelValue.value.length,
            available_from: null,
            available_to: null,
        },
    ];
};

const removeTicket = (index: number) => {
    if (confirm(lang('congresses::congresses.tickets.confirm_remove'))) {
        modelValue.value = modelValue.value.filter((_, idx) => idx !== index);
    }
};

const onReordered = (event: { oldIndex: number; newIndex: number }) => {
    const items = [...modelValue.value];
    const [movedItem] = items.splice(event.oldIndex, 1);
    items.splice(event.newIndex, 0, movedItem);

    items.forEach((ticket, idx) => {
        ticket.sort_order = idx;
    });

    modelValue.value = items;
};
</script>
